import React, { useCallback, useRef, useState } from 'react';
import ConfirmDialog from './ConfirmDialog';

type ConfirmOptions = { title: string; message?: React.ReactNode; confirmLabel: string; cancelLabel: string };

/**
 * `await confirm({...})` → true/false, shown with the kid-style ConfirmDialog instead of
 * window.confirm. Render the returned `dialog` once in the component.
 */
export function useConfirm(): { confirm: (o: ConfirmOptions) => Promise<boolean>; dialog: React.ReactElement } {
  const [opts, setOpts] = useState<ConfirmOptions | null>(null);
  const resolver = useRef<((ok: boolean) => void) | null>(null);

  const confirm = useCallback((o: ConfirmOptions) => {
    resolver.current?.(false); // a newer question replaces an unanswered one
    setOpts(o);
    return new Promise<boolean>((resolve) => { resolver.current = resolve; });
  }, []);

  const answer = useCallback((ok: boolean) => {
    resolver.current?.(ok);
    resolver.current = null;
    setOpts(null);
  }, []);

  const onCancel = useCallback(() => answer(false), [answer]);

  const dialog = (
    <ConfirmDialog
      open={!!opts}
      title={opts?.title || ''}
      message={opts?.message}
      confirmLabel={opts?.confirmLabel || 'OK'}
      cancelLabel={opts?.cancelLabel || ''}
      onCancel={onCancel}
      onConfirm={() => answer(true)}
    />
  );

  return { confirm, dialog };
}
